import { forwardRef, type HTMLAttributes, type ReactNode } from "react";
import { cx } from "../utils/cx";

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Hover/press affordance + keyboard focusability for clickable cards. */
  readonly interactive?: boolean;
  /** Drops the resting shadow (dense dashboards, nested cards). */
  readonly flat?: boolean;
}

export const Card = forwardRef<HTMLDivElement, CardProps>(function Card(
  { interactive = false, flat = false, className, children, ...rest },
  ref,
) {
  return (
    <div
      ref={ref}
      className={cx("ui-card", interactive && "ui-card--interactive", flat && "ui-card--flat", className)}
      tabIndex={interactive ? 0 : undefined}
      role={interactive ? "button" : undefined}
      {...rest}
    >
      {children}
    </div>
  );
});

export interface CardHeaderProps {
  readonly title: ReactNode;
  /** Actions rendered at the logical end of the header row. */
  readonly actions?: ReactNode;
  readonly className?: string;
}

export function CardHeader({ title, actions, className }: CardHeaderProps) {
  return (
    <div className={cx("ui-card-header", className)}>
      <h3 className="ui-card-title">{title}</h3>
      {actions}
    </div>
  );
}

export function CardBody({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cx("ui-card-body", className)} {...rest} />;
}

export function CardFooter({ className, ...rest }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cx("ui-card-footer", className)} {...rest} />;
}
